import { LiveFeedback } from '@/components/LiveFeedback';
import { APP_ROUTES } from '@/config/constants';
import { ButtonOutlineSmall, ButtonPrimarySmall, useSnackbar } from '@/design-system';
import { SAVE_USER_FAVORITE_ORG_MUTATION } from '@/graphql/mutation';
import { GET_USER_FAVORITE_ORGS } from '@/graphql/queries';
import { GetUserFavoriteOrgsResponse, SaveUserFavoriteOrgResponse } from '@/graphql/types';
import { useLiveFeedback } from '@/hooks';
import { useMutation, useQuery } from '@apollo/client';
import { css } from '@emotion/css';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface FollowButtonProps {
  orgId: number;
  orgName: string;
  onClick?: () => void;
  showViewFollowingCta?: boolean;
  isFollowingInitial?: boolean;
}

export const FollowButton: React.FC<FollowButtonProps> = ({
  orgId,
  orgName,
  onClick,
  showViewFollowingCta = true,
  isFollowingInitial = false,
}) => {
  const [isFollowing, setIsFollowing] = useState(isFollowingInitial);
  const showToast = useSnackbar();
  const navigate = useNavigate();
  const [feedbackMessage, announce] = useLiveFeedback({ delay: 2000 });
  const [saveUserFavoriteOrg, { loading: mutationLoading }] =
    useMutation<SaveUserFavoriteOrgResponse>(SAVE_USER_FAVORITE_ORG_MUTATION, {
      onCompleted: result => {
        const saved = result.saveUserFavoriteOrg.isSaved;
        const message = saved
          ? 'You’re now following this organization.'
          : `You’ve unfollowed ${orgName}.`;

        setIsFollowing(saved);
        announce(message);
        if (saved) {
          showToast(
            showViewFollowingCta
              ? {
                  message,
                  buttonLabel: 'View Following',
                  onClick: () =>
                    navigate(APP_ROUTES.accountInformation, {
                      state: { expandSection: 'following-organizations' },
                    }),
                }
              : { message }
          );
        } else {
          showToast({
            message,
            buttonLabel: 'Undo',
            onClick: () => saveUserFavoriteOrg({ variables: { input: { orgId, isSaved: true } } }),
          });
        }
      },
      onError: () => {
        setIsFollowing(isFollowing);
        announce('Could not update follow status. Please try again.');
      },
    });

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    saveUserFavoriteOrg({ variables: { input: { orgId, isSaved: !isFollowing } } });
    onClick?.();
  };

  const Button = isFollowing ? ButtonOutlineSmall : ButtonPrimarySmall;

  return (
    <>
      <Button
        aria-label={`${isFollowing ? 'Unfollow' : 'Follow'} ${orgName}`}
        onClick={handleClick}
        className={cn.button}
        loading={mutationLoading}
      >
        {isFollowing ? 'Following' : 'Follow'}
      </Button>
      <LiveFeedback message={feedbackMessage} />
    </>
  );
};

const cn = {
  button: css`
    gap: 8px !important;
  `,
};
